import { useMemo } from "react";
import * as React from "react";
import {
  Box,
  FormControl,
  Select as MuiSelect,
  MenuItem,
  InputLabel,
  type SelectChangeEvent,
  IconButton,
  Checkbox as MuiCheckbox,
  ListItemText,
  Chip,
  FormHelperText,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { type ISelectObject } from "@/types/select";
import { SizingWrapper } from "@/components/Wrapper";
import { type IProps as MultiSelectIProps } from "@/components/Select/MultiSelect";

export interface IProps extends MultiSelectIProps {
  /**
   * ラベルとセレクト本体を紐付けるためのID
   * 同一画面に複数配置する場合はインスタンスごとに一意な値を指定する
   */
  id?: string;
}

/** メニューの先頭に固定表示する「選択をクリア」項目 */
export const CLEAR_SELECTION_OPTION: ISelectObject = {
  value: "all",
  label: "選択をクリア",
};

/** メニューの先頭に表示する「すべて選択」項目（選択上限がない場合のみ） */
export const SELECT_ALL_OPTION: ISelectObject = {
  value: "select_all",
  label: "すべて選択",
};

const DEFAULT_ID = "feed-reception-reason";

/**
 * 商業登記受付帳の検索条件で使うチェックボックス付き複数選択セレクト
 * - 選択肢は複数選択できる。maxSelect を指定した場合は上限に達した時点で未選択の選択肢を非活性にする
 * - maxSelect 未指定時はメニュー先頭の「すべて選択」で全選択でき、全選択中はチップを「すべて（N種類）」の1つにまとめる
 * - メニュー先頭の「選択をクリア」またはクリアボタンで選択を全て解除する
 * @param props
 */
const FeedReceptionReasonSelect: React.FC<IProps> = (props) => {
  const {
    id = DEFAULT_ID,
    label,
    options,
    value,
    onChange,
    isClearable = true,
    maxSelect,
    size,
    error,
    formHelperText,
  } = props;

  const selectId = `${id}-id`;
  const labelId = `${id}-label`;

  const selectedOptions = useMemo(
    () => options.filter((option) => value.includes(option.value)),
    [options, value]
  );

  const canSelectAll = maxSelect == null;
  const isAllSelected =
    canSelectAll &&
    options.length > 0 &&
    options.every((option) => value.includes(option.value));

  /**
   * 選択変更時の処理
   * 「選択をクリア」が選ばれた場合は全解除、「すべて選択」が選ばれた場合は全選択、それ以外は選択された値をそのまま反映する
   */
  const handleChange = (event: SelectChangeEvent<string[]>): void => {
    const selectedValues = event.target.value;
    // MUIのSelectはautofill時に文字列で値を渡すことがあるため配列に正規化する
    const selectedValueList =
      typeof selectedValues === "string" ? [selectedValues] : selectedValues;

    if (selectedValueList.includes(CLEAR_SELECTION_OPTION.value)) {
      onChange([]);
      return;
    }
    if (selectedValueList.includes(SELECT_ALL_OPTION.value)) {
      onChange(options.map((option) => option.value));
      return;
    }
    onChange(selectedValueList);
  };

  const handleClearClick = (): void => {
    onChange([]);
  };

  /**
   * 選択肢を非活性にするかを判定する
   * 選択上限（maxSelect）に達している場合、未選択の選択肢を非活性にする
   */
  const getDisabled = (option: ISelectObject): boolean => {
    if (maxSelect == null) {
      return false;
    }
    return value.length >= maxSelect && !value.includes(option.value);
  };

  return (
    <SizingWrapper size={size}>
      <FormControl fullWidth error={error}>
        <InputLabel id={labelId}>{label}</InputLabel>
        <MuiSelect
          id={selectId}
          labelId={labelId}
          label={label}
          onChange={handleChange}
          multiple
          value={value}
          defaultValue={[]}
          error={error}
          sx={{
            [`#${selectId}`]: {
              pr: 0,
            },
          }}
          renderValue={() => (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
              }}
            >
              {isAllSelected ? (
                <Chip
                  label={`すべて（${options.length}種類）`}
                  sx={{ cursor: "pointer" }}
                />
              ) : (
                selectedOptions.map((option) => (
                  <Chip
                    key={option.value}
                    label={option.label}
                    sx={{ cursor: "pointer" }}
                  />
                ))
              )}
            </Box>
          )}
          endAdornment={
            isClearable ? (
              <IconButton
                aria-label="選択を全て解除"
                sx={{
                  display:
                    selectedOptions.length === 0 ? "none" : "inline-flex",
                  marginRight: "10px",
                }}
                onClick={handleClearClick}
              >
                <ClearIcon />
              </IconButton>
            ) : (
              <></>
            )
          }
        >
          {canSelectAll ? (
            <MenuItem
              key={SELECT_ALL_OPTION.value}
              value={SELECT_ALL_OPTION.value}
              // 選択肢の取得前に押すと空の選択になり、種類で絞らない検索になるため非活性にする
              disabled={options.length === 0}
              sx={{ my: 1 }}
            >
              <ListItemText primary={SELECT_ALL_OPTION.label} />
            </MenuItem>
          ) : null}
          <MenuItem
            key={CLEAR_SELECTION_OPTION.value}
            value={CLEAR_SELECTION_OPTION.value}
            sx={{ my: 1 }}
          >
            <ListItemText primary={CLEAR_SELECTION_OPTION.label} />
          </MenuItem>
          {options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={getDisabled(option)}
            >
              <MuiCheckbox checked={value.includes(option.value)} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </MuiSelect>
        {formHelperText ? (
          <FormHelperText>{formHelperText}</FormHelperText>
        ) : null}
      </FormControl>
    </SizingWrapper>
  );
};

export { FeedReceptionReasonSelect };
